export const CONSENT_COOKIE_NAME = 'solva_consent'
export const ANONYMOUS_ID_COOKIE_NAME = 'solva_cid'

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60

type ConsentValue = 'all' | 'necessary'

function parseCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.split('; ').find(row => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.split('=')[1]) : null
}

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const host = window.location.hostname
  if (!host || host === 'localhost' || host === '127.0.0.1') return undefined
  if (host.endsWith('ngrok-free.app') || host.endsWith('ngrok.io')) return undefined

  // Custom ngrok.app domains: .ngrok.app is on the public suffix list,
  // so we need to use the custom subdomain as the cookie domain.
  // e.g. web.tommy-local.ngrok.app -> .tommy-local.ngrok.app
  if (host.endsWith('.ngrok.app')) {
    const parts = host.split('.')
    if (parts.length >= 3) return `.${parts.slice(-3).join('.')}`
    return undefined
  }

  const parts = host.split('.')
  if (parts.length <= 2) return `.${host}`
  return `.${parts.slice(-2).join('.')}`
}

function isSecureContext(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:'
}

function buildAttributes(maxAge: number): string {
  const secure = isSecureContext()
  const attrs = [`Max-Age=${maxAge}`, 'Path=/', 'SameSite=Lax']
  if (secure) attrs.push('Secure')
  const domain = getCookieDomain()
  if (domain) attrs.push(`Domain=${domain}`)
  return attrs.join('; ')
}

export function getConsentCookie(): ConsentValue | null {
  const value = parseCookie(CONSENT_COOKIE_NAME)
  if (value === 'all' || value === 'necessary') return value
  return null
}

export function setConsentCookie(value: ConsentValue): void {
  if (typeof document === 'undefined') return
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; ${buildAttributes(ONE_YEAR_SECONDS)}`
}

export function getOrCreateAnonymousId(): string {
  const existing = parseCookie(ANONYMOUS_ID_COOKIE_NAME)
  if (existing) return existing

  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = (Math.random() * 16) | 0
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
        })
  if (typeof document !== 'undefined') {
    document.cookie = `${ANONYMOUS_ID_COOKIE_NAME}=${id}; ${buildAttributes(ONE_YEAR_SECONDS)}`
  }
  return id
}

export function clearConsentCookies(): void {
  if (typeof document === 'undefined') return
  const domain = getCookieDomain()
  const secure = isSecureContext()
  const base = `Max-Age=0; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
  const domainAttr = domain ? `; Domain=${domain}` : ''
  document.cookie = `${CONSENT_COOKIE_NAME}=; ${base}${domainAttr}`
  document.cookie = `${ANONYMOUS_ID_COOKIE_NAME}=; ${base}${domainAttr}`
}

const SYNC_KEY = 'solva_consent_synced'

export function isConsentSynced(): boolean {
  if (typeof window === 'undefined') return true
  const consent = getConsentCookie()
  if (!consent) return true
  const anonymousId = getOrCreateAnonymousId()
  return localStorage.getItem(SYNC_KEY) === `${anonymousId}:${consent}`
}

export function markConsentSynced(): void {
  if (typeof window === 'undefined') return
  const consent = getConsentCookie()
  if (!consent) return
  const anonymousId = getOrCreateAnonymousId()
  localStorage.setItem(SYNC_KEY, `${anonymousId}:${consent}`)
}
